import { AxiosError } from 'axios';

import { Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';

import { AlertError, AlertProblem, AlertWarning } from './AlertComponent';
import { ErrorUtils } from './ErrorUtils.ts';

export const useModal = () => {
    const showServerErrorModal = (
        alertSummary: string,
        error: Error | AxiosError | null
    ) => {
        const errorInfo = ErrorUtils.extractErrorDetails(error);
        if (errorInfo == null) return;

        const detailsElement = wrapElements([
            <Text
                key={0}
            >{`Title: ${errorInfo.title ?? 'No error title available.'}`}</Text>,
            <Text key={1} style={{ whiteSpace: 'pre-wrap' }}>{`Details: ${
                errorInfo.detail ?? 'No additional error details available.'
            }`}</Text>,
            <Text
                key={2}
            >{`Status Code: ${errorInfo.responseCode ?? 'No status code available'}`}</Text>
        ]);

        showModal(
            <AlertError summary={alertSummary} details={detailsElement} />
        );
    };

    const showProblemModal = (alertSummary: string, details: string[]) => {
        const detailsElement = wrapElements(
            details.map((d, index) => <Text key={index}>{d}</Text>)
        );

        showModal(
            <AlertProblem summary={alertSummary} details={detailsElement} />
        );
    };

    const showWarningModal = (alertSummary: string, details: string[]) => {
        const detailsElement = wrapElements(
            details.map((d, index) => <Text key={index}>{d}</Text>)
        );

        showModal(
            <AlertWarning summary={alertSummary} details={detailsElement} />
        );
    };

    const showCannotUploadCsv = (
        uploadType: string,
        errors: string[],
        fileName: string
    ) => {
        showProblemModal(
            `${uploadType} csv file "${fileName}" cannot be uploaded`,
            [
                ...errors,
                `Please check the file is formatted correctly, ensure the file has a minimum of one populated data row and try again. `
            ]
        );
    };

    const showDeleteConfirmModal = (
        alertSummary: string,
        details: string,
        onConfirm: () => void
    ) => {
        showConfirmModal(alertSummary, details, onConfirm, 'Delete', 'Cancel');
    };

    const showArchiveConfirmModal = (
        alertSummary: string,
        details: string,
        onConfirm: () => void
    ) => {
        showConfirmModal(alertSummary, details, onConfirm, 'Archive', 'Cancel');
    };

    const showYesNoConfirmWarningModal = (
        alertSummary: string,
        details: string,
        onConfirm: () => void
    ) => {
        showConfirmModal(alertSummary, details, onConfirm, 'Yes', 'No');
    };

    const showConfirmModal = (
        alertSummary: string,
        details: string,
        onConfirm: () => void,
        confirmButtonLabel: string,
        canelButtonLabel: string
    ) => {
        const config = {
            labels: { confirm: confirmButtonLabel, cancel: canelButtonLabel },
            onConfirm
        };

        const detailsElement = wrapElements([<Text key={0}>{details}</Text>]);

        showModal(
            <AlertWarning summary={alertSummary} details={detailsElement} />,
            config
        );
    };

    const showModal = (content: JSX.Element[] | JSX.Element, config = {}) => {
        modals.openConfirmModal({
            size: 'lg',
            p: 'md',
            withCloseButton: false,
            children: <Stack gap={'md'}>{content}</Stack>,
            labels: { confirm: 'Ok', cancel: 'Close' },
            onCancel: () => {
                return;
            },
            onConfirm: () => {
                return;
            },
            ...config
        });
    };

    const wrapElements = (elements: JSX.Element[]) => (
        <Stack gap={'md'}>{elements}</Stack>
    );

    return {
        showModal,
        showServerErrorModal,
        showProblemModal,
        showWarningModal,
        showDeleteConfirmModal,
        showArchiveConfirmModal,
        showConfirmWarningModal: showYesNoConfirmWarningModal,
        showCannotUploadCsvModal: showCannotUploadCsv
    };
};
